
export interface IterationToolAction {
  toolName: string;
  success: boolean;
  argsSummary: string;
  /** Tool's result (or error) payload, truncated for diagnostics display. Absent for pre-existing rows. */
  resultSummary?: string;
}

/** Which agent made an LLM call: the chat agent itself, or the Data Analyst sub-agent it delegated to. */
export type IterationUsageSource = 'main' | 'data_analyst';

/** Identifies a run and, for Data Analyst rounds, the main run that started them. All optional: older rows lack them. */
export interface IterationUsageMeta {
  turnId?: string;
  source?: IterationUsageSource;
  /** Data Analyst rounds: the main-agent iteration (same turn) whose Data_Analyst call started this round. */
  parentIteration?: number | null;
  /** Data Analyst rounds: the main agent's Data_Analyst tool_call id - one id per analyst run. */
  analystCallId?: string | null;
  resultName?: string | null;
  /** Text the model wrote in this call - on the last main run, the answer the user saw. */
  llmResponseText?: string | null;
}

export interface IterationUsageEntry extends IterationUsageMeta {
  iteration: number;
  totalTokens: number;
  tools: IterationToolAction[];
  llmRequestPayload?: string | null;
  /** Tokens served from Anthropic's prompt cache on this call (~0.1x price of a fresh input token). Absent for pre-existing rows that predate this field. */
  cacheReadTokens?: number;
  /** ISO timestamp of when this LLM call was recorded (arrival time for live runs). Null/absent for rows that predate it. */
  createdAt?: string | null;
}

export interface IterationUsageTurnRow extends IterationUsageMeta {
  turnId: string;
  kind?: 'turn';
  iteration: number;
  totalTokens: number;
  tools: IterationToolAction[];
  llmRequestPayload?: string | null;
  cacheReadTokens?: number;
  createdAt?: string | null;
}

export interface IterationUsageSummarizationRow {
  turnId: string;
  kind: 'summarization';
  llmCallCount: number;
  totalTokens: number;
  cacheReadTokens?: number;
}

export type IterationUsageRow = IterationUsageTurnRow | IterationUsageSummarizationRow;

/** A history-summarization event for one turn (SuperAdmin debug popup only).
 *  Live turns (via SSE) carry summaryCount/narrativeSummary; reloaded historical
 *  turns (via the iteration-usage REST endpoint) only have call-count/token totals -
 *  the DB only persists the conversation's CURRENT summary, not per-turn snapshots. */
export type HistorySummarizationEntry =
  | { turnId: string; summaryCount: number; llmCallCount: number; totalTokens: number; narrativeSummary: string }
  | { turnId: string; llmCallCount: number; totalTokens: number }
  | { turnId: string; failed: true; error: string };

/** Sums nanodollar strings, ignoring null/undefined/malformed entries. Returns null if none were present. */
// function sumCostUsdNano(values: Array<string | null | undefined>): string | null {
//   let sum: bigint | null = null;
//   for (const v of values) {
//     if (v === null || v === undefined) continue;
//     try {
//       sum = (sum ?? 0n) + BigInt(v);
//     } catch {
//       // malformed - skip rather than throw
//     }
//   }
//   return sum === null ? null : sum.toString();
// }

export function summarizeIterationUsage(entries: IterationUsageEntry[]): {
  runCount: number;
  totalTokens: number;
  totalCacheReadTokens: number;
  label: string;
} | null {
  if (!entries.length) return null;
  const totalTokens = entries.reduce((sum, entry) => sum + entry.totalTokens, 0);
  const totalCacheReadTokens = entries.reduce((sum, entry) => sum + (entry.cacheReadTokens ?? 0), 0);
  const runCount = entries.length;
  const analystRunCount = entries.filter(isAnalystEntry).length;

  // "cached" is parenthesized against the token count (not · -joined as its own stat) because
  // cacheReadTokens is already summed INTO totalTokens on the backend - it's a breakdown of that
  // number, not an additional count on top of it. See recordAnthropicCallSafely/recordCall.
  const tokenPart = totalCacheReadTokens > 0
    ? `${totalTokens.toLocaleString()} Tokens (${totalCacheReadTokens.toLocaleString()} cached)`
    : `${totalTokens.toLocaleString()} Tokens`;
  const runPart = analystRunCount > 0
    ? `${runCount - analystRunCount} main + ${analystRunCount} analyst runs`
    : `${runCount} run${runCount === 1 ? '' : 's'}`;
  const parts = [runPart, tokenPart];

  return {
    runCount,
    totalTokens,
    totalCacheReadTokens,
    label: parts.join(' · '),
  };
}

export function formatHistorySummarizationLine(entry: HistorySummarizationEntry): string {
  if ('failed' in entry) {
    return `Summarization failed: ${entry.error}`;
  }
  const parts = [
    'summaryCount' in entry ? `Summary #${entry.summaryCount}` : 'Summarized',
    `${entry.llmCallCount} call${entry.llmCallCount === 1 ? '' : 's'}`,
    `${entry.totalTokens.toLocaleString()} Tokens`,
  ];
  return parts.join(' · ');
}

function formatRunTime(createdAt: string | null | undefined): string | null {
  if (!createdAt) return null;
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString('en-GB', { hour12: false });
}

/**
 * `position` is the run's 1-based place in the chronological list. `iteration` restarts at 1 on every
 * turn, so a bubble that spans several turns would otherwise show "Run #1" more than once.
 */
export function formatIterationUsageLine(entry: IterationUsageEntry, position: number = entry.iteration): string {
  // Same containment note as summarizeIterationUsage above: cacheReadTokens is a subset of
  // totalTokens, so it's parenthesized against it rather than listed as its own · -joined stat.
  const tokenPart = entry.cacheReadTokens
    ? `${entry.totalTokens.toLocaleString()} Tokens (${entry.cacheReadTokens.toLocaleString()} cached)`
    : `${entry.totalTokens.toLocaleString()} Tokens`;
  const parts = [`Run #${position}: ${tokenPart}`];
  const time = formatRunTime(entry.createdAt);
  if (time) parts.push(time);
  return parts.join(' · ');
}

/** Analyst round 0 is the requirements check that reviews the task before the analyst's own rounds (1..n). */
export const REQUIREMENTS_CHECK_ROUND = 0;

export function formatAnalystRoundLine(round: IterationUsageEntry): string {
  const line = formatIterationUsageLine(round, round.iteration);
  return round.iteration === REQUIREMENTS_CHECK_ROUND
    ? line.replace(/^Run #0/, 'Requirements check')
    : line.replace(/^Run #/, 'Round #');
}

export function formatIterationToolAction(action: IterationToolAction): string {
  const status = action.success ? '✓' : '✗';
  return `${status} ${action.toolName}`;
}

export function formatArgsForModal(argsSummary: string): string {
  if (!argsSummary) return '(no args)';
  try {
    return JSON.stringify(JSON.parse(argsSummary), null, 2);
  } catch {
    return argsSummary;
  }
}

const ARGS_INLINE_MAX_LEN = 160;

/** One-line, compact rendering of argsSummary for the row itself (full value stays in the title tooltip). */
export function formatArgsInline(argsSummary: string): string {
  if (!argsSummary) return '(no args)';
  let compact: string;
  try {
    compact = JSON.stringify(JSON.parse(argsSummary));
  } catch {
    compact = argsSummary;
  }
  return compact.length > ARGS_INLINE_MAX_LEN
    ? `${compact.slice(0, ARGS_INLINE_MAX_LEN)}…`
    : compact;
}

const RESULT_MODAL_MAX_LEN = 2000;

export function formatResultForModal(resultSummary: string | null | undefined): string {
  if (!resultSummary) return '(not available)';
  let formatted: string;
  try {
    formatted = JSON.stringify(JSON.parse(resultSummary), null, 2);
  } catch {
    formatted = resultSummary;
  }
  return formatted.length > RESULT_MODAL_MAX_LEN
    ? `${formatted.slice(0, RESULT_MODAL_MAX_LEN)}\n… (truncated)`
    : formatted;
}

export function formatLlmRequestForModal(payload: string | null | undefined): string {
  if (!payload) return '(not available)';
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
}

export function isAnalystEntry(entry: IterationUsageEntry): boolean {
  return entry.source === 'data_analyst';
}

/** One Data_Analyst run: all rounds the sub-agent made for a single Data_Analyst call of the main agent. */
export interface AnalystRunGroup {
  key: string;
  resultName: string | null;
  rounds: IterationUsageEntry[];
  totalTokens: number;
  cacheReadTokens: number;
}

export interface MainRunWithAnalysts {
  entry: IterationUsageEntry;
  /** 1-based place among the main runs, for the "Run #n" label. */
  position: number;
  analystRuns: AnalystRunGroup[];
}

export interface IterationUsageView {
  mainRuns: MainRunWithAnalysts[];
  /** Every analyst run, in the order they happened. */
  analystRuns: AnalystRunGroup[];
  /** Analyst runs whose parent main run isn't in this bubble (shown at the end of the combined view). */
  orphanAnalystRuns: AnalystRunGroup[];
  mainTotals: { runCount: number; totalTokens: number; cacheReadTokens: number };
  analystTotals: { runCount: number; totalTokens: number; cacheReadTokens: number };
}

function totals(entries: IterationUsageEntry[]) {
  return {
    runCount: entries.length,
    totalTokens: entries.reduce((sum, e) => sum + e.totalTokens, 0),
    cacheReadTokens: entries.reduce((sum, e) => sum + (e.cacheReadTokens ?? 0), 0),
  };
}

/** Splits a bubble's runs into main runs and Data Analyst runs, nesting each analyst run under the main run that called it. */
export function buildIterationUsageView(entries: IterationUsageEntry[]): IterationUsageView {
  const mainEntries = entries.filter((e) => !isAnalystEntry(e));
  const analystEntries = entries.filter(isAnalystEntry);

  const groups = new Map<string, AnalystRunGroup & { parentKey: string }>();
  for (const round of analystEntries) {
    const key = `${round.turnId ?? ''}:${round.analystCallId ?? `p${round.parentIteration ?? '?'}`}`;
    const group = groups.get(key) ?? {
      key,
      parentKey: `${round.turnId ?? ''}:${round.parentIteration ?? ''}`,
      resultName: round.resultName ?? null,
      rounds: [],
      totalTokens: 0,
      cacheReadTokens: 0,
    };
    group.rounds.push(round);
    group.totalTokens += round.totalTokens;
    group.cacheReadTokens += round.cacheReadTokens ?? 0;
    groups.set(key, group);
  }
  for (const group of groups.values()) group.rounds.sort((a, b) => a.iteration - b.iteration);

  // Each analyst run goes under exactly ONE main run. The iteration number alone is ambiguous: in the
  // widget's client-tool mode every round trip is a new request that restarts at iteration 1, so the
  // turn can have several "iteration 1" runs. Prefer the candidate that actually called Data_Analyst.
  const mainRuns: MainRunWithAnalysts[] = mainEntries.map((entry, i) => ({ entry, position: i + 1, analystRuns: [] }));
  const attached = new Set<string>();
  for (const group of groups.values()) {
    const candidates = mainRuns.filter((r) => `${r.entry.turnId ?? ''}:${r.entry.iteration}` === group.parentKey);
    // A run takes as many analyst runs as it made Data_Analyst calls, so two round trips that each
    // called it don't both claim the first one
    const parent = candidates.find((r) =>
      r.entry.tools.filter((t) => t.toolName === 'Data_Analyst').length > r.analystRuns.length)
      ?? candidates[candidates.length - 1];
    if (!parent) continue;
    parent.analystRuns.push(group);
    attached.add(group.key);
  }

  const allGroups = [...groups.values()];
  return {
    mainRuns,
    analystRuns: allGroups,
    orphanAnalystRuns: allGroups.filter((g) => !attached.has(g.key)),
    mainTotals: totals(mainEntries),
    analystTotals: totals(analystEntries),
  };
}

export function formatAnalystRunHeader(group: AnalystRunGroup): string {
  const tokenPart = group.cacheReadTokens
    ? `${group.totalTokens.toLocaleString()} Tokens (${group.cacheReadTokens.toLocaleString()} cached)`
    : `${group.totalTokens.toLocaleString()} Tokens`;
  const rounds = `${group.rounds.length} round${group.rounds.length === 1 ? '' : 's'}`;
  return `Data_Analyst → ${group.resultName ?? '(unnamed)'} · ${rounds} · ${tokenPart}`;
}

export function formatTotalsLine(label: string, t: { runCount: number; totalTokens: number; cacheReadTokens: number }): string {
  const tokenPart = t.cacheReadTokens
    ? `${t.totalTokens.toLocaleString()} tokens (${t.cacheReadTokens.toLocaleString()} cached)`
    : `${t.totalTokens.toLocaleString()} tokens`;
  return `${label}: ${t.runCount} run${t.runCount === 1 ? '' : 's'} · ${tokenPart}`;
}

/** Which runs the usage view shows: everything nested, only the main agent, or only the Data Analyst. */
export type IterationUsageTab = 'combined' | 'main' | 'analyst';

export const ITERATION_USAGE_TAB_LABELS: Record<IterationUsageTab, string> = {
  combined: 'Interaction (all)',
  main: 'Main agent',
  analyst: 'Data Analyst',
};

function parseIfJson(value: string | null | undefined): unknown {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function entryToJson(entry: IterationUsageEntry) {
  return {
    iteration: entry.iteration,
    turnId: entry.turnId ?? null,
    source: entry.source ?? 'main',
    totalTokens: entry.totalTokens,
    cacheReadTokens: entry.cacheReadTokens ?? 0,
    createdAt: entry.createdAt ?? null,
    llmRequest: parseIfJson(entry.llmRequestPayload),
    llmResponse: entry.llmResponseText ?? null,
    tools: entry.tools.map((tool) => ({
      toolName: tool.toolName,
      success: tool.success,
      request: parseIfJson(tool.argsSummary),
      response: parseIfJson(tool.resultSummary),
    })),
  };
}

function analystRunToJson(group: AnalystRunGroup) {
  return {
    resultName: group.resultName,
    totalTokens: group.totalTokens,
    cacheReadTokens: group.cacheReadTokens,
    rounds: group.rounds.map((round) => ({
      step: round.iteration === REQUIREMENTS_CHECK_ROUND ? 'requirements_check' : 'analysis',
      ...entryToJson(round),
    })),
  };
}

/** The flow shown in the given tab as JSON - full requests and responses, for tooling or comparing runs. */
export function formatFlowAsJson(
  entries: IterationUsageEntry[],
  tab: IterationUsageTab,
  summarizationEvents: HistorySummarizationEntry[] = [],
): string {
  const view = buildIterationUsageView(entries);
  const flow: Record<string, unknown> = {
    tab,
    totals: { main: view.mainTotals, analyst: view.analystTotals },
  };
  if (tab !== 'analyst' && summarizationEvents.length) flow.historySummarization = summarizationEvents;

  if (tab === 'analyst') {
    flow.analystRuns = view.analystRuns.map(analystRunToJson);
  } else {
    flow.runs = view.mainRuns.map((run) => ({
      run: run.position,
      ...entryToJson(run.entry),
      ...(tab === 'combined' ? { analystRuns: run.analystRuns.map(analystRunToJson) } : {}),
    }));
    if (tab === 'combined' && view.orphanAnalystRuns.length) {
      flow.unattachedAnalystRuns = view.orphanAnalystRuns.map(analystRunToJson);
    }
  }
  return JSON.stringify(flow, null, 2);
}

function fenced(text: string, lang: string): string {
  // A longer fence than any backtick run inside keeps payloads that contain ``` intact
  const longestRun = Math.max(2, ...(text.match(/`+/g) ?? []).map((run) => run.length));
  const fence = '`'.repeat(longestRun + 1);
  return `${fence}${lang}\n${text}\n${fence}`;
}

function entryToMarkdown(entry: IterationUsageEntry, title: string, level: number): string {
  const h = '#'.repeat(level);
  const parts = [`${h} ${title}`, `${h}# Sent to LLM`, fenced(formatLlmRequestForModal(entry.llmRequestPayload), 'json')];
  if (entry.llmResponseText) parts.push(`${h}# LLM response`, fenced(entry.llmResponseText, 'text'));
  parts.push(`${h}# Tools`);
  if (!entry.tools.length) {
    parts.push('No tool calls in this run');
  } else {
    for (const tool of entry.tools) {
      parts.push(
        `- ${formatIterationToolAction(tool)}`,
        'Request:',
        fenced(formatArgsForModal(tool.argsSummary), tool.toolName.startsWith('run_sql') ? 'sql' : 'json'),
        'Response:',
        fenced(tool.resultSummary ? (JSON.stringify(parseIfJson(tool.resultSummary), null, 2) ?? '') : '(not available)', 'json'),
      );
    }
  }
  return parts.join('\n\n');
}

function analystRunToMarkdown(group: AnalystRunGroup, level: number): string {
  const h = '#'.repeat(level);
  const rounds = group.rounds.map((round) => entryToMarkdown(round, formatAnalystRoundLine(round), level + 1));
  return [`${h} [Analyst] ${formatAnalystRunHeader(group)}`, ...rounds].join('\n\n');
}

/** The flow shown in the given tab as readable Markdown - for pasting into a ticket or a chat. */
export function formatFlowAsMarkdown(
  entries: IterationUsageEntry[],
  tab: IterationUsageTab,
  summarizationEvents: HistorySummarizationEntry[] = [],
): string {
  const view = buildIterationUsageView(entries);
  const hasAnalyst = view.analystRuns.length > 0;
  const parts = [`# LLM flow - ${ITERATION_USAGE_TAB_LABELS[tab]}`];
  parts.push(hasAnalyst
    ? `${formatTotalsLine('Main', view.mainTotals)} | ${formatTotalsLine('Analyst', view.analystTotals)}`
    : formatTotalsLine('Total', view.mainTotals));

  if (tab !== 'analyst' && summarizationEvents.length) {
    parts.push('## History Summarization', ...summarizationEvents.map((e) => `- ${formatHistorySummarizationLine(e)}`));
  }

  if (tab === 'analyst') {
    parts.push(...view.analystRuns.map((group) => analystRunToMarkdown(group, 2)));
  } else {
    for (const run of view.mainRuns) {
      const label = `${hasAnalyst ? '[Main] ' : ''}${formatIterationUsageLine(run.entry, run.position)}`;
      parts.push(entryToMarkdown(run.entry, label, 2));
      if (tab === 'combined') parts.push(...run.analystRuns.map((group) => analystRunToMarkdown(group, 3)));
    }
    if (tab === 'combined') parts.push(...view.orphanAnalystRuns.map((group) => analystRunToMarkdown(group, 2)));
  }
  return `${parts.join('\n\n')}\n`;
}
